import Store from 'repatch';

import State from '../core/State';
import YoutubeChannel from '../entity/YoutubeChannel';
import ErrorPresenter from '../service/ErrorPresenter';
import GoogleOauthService from '../service/GoogleOauthService';
import MessageDispatcher from '../service/MessageDispatcher';
import { ChannelUnlinkedError } from '../service/persistent/GoogleWebApi';
import YoutubeWebApi from '../service/persistent/YoutubeWebApi';
import { setUploadedAdPlatformKeys } from '../thunk/creativeSending';
import {
  decrement as decrementFullScreenSpinnerNecessity,
  increment as incrementFullScreenSpinnerNecessity,
  setLoadingMessage,
} from '../thunk/fullScreenSpinner';
import {
  addYoutubeChannel,
  disposeYoutubeChannel,
  disposeYoutubeChannels,
  setYoutubeChannels,
} from '../thunk/youtubeChannels';

class SendCreativeToYoutubeUsecase {
  private store: Store<State>;
  private errorPresenter: ErrorPresenter;
  private messageDispatcher: MessageDispatcher;
  private youtubeChannelConnectionService: GoogleOauthService;
  private youtubeWebApi: YoutubeWebApi;

  constructor({
    store,
    errorPresenter,
    messageDispatcher,
    youtubeChannelConnectionService,
    youtubeWebApi,
  }: {
    store: Store<State>;
    errorPresenter: ErrorPresenter;
    messageDispatcher: MessageDispatcher;
    youtubeChannelConnectionService: GoogleOauthService;
    youtubeWebApi: YoutubeWebApi;
  }) {
    this.store = store;
    this.errorPresenter = errorPresenter;
    this.messageDispatcher = messageDispatcher;
    this.youtubeChannelConnectionService = youtubeChannelConnectionService;
    this.youtubeWebApi = youtubeWebApi;
  }

  public fetchChannels = async (): Promise<boolean | void> => {
    this.store.dispatch(incrementFullScreenSpinnerNecessity());

    try {
      const channels = await this.youtubeWebApi.getAllConnectedChannels();
      this.store.dispatch(setYoutubeChannels(channels));

      return true;
    } catch (err) {
      this.errorPresenter.showAsNonFatal(err);
    } finally {
      this.store.dispatch(decrementFullScreenSpinnerNecessity());
    }
  };

  public sendCreative = async (
    deliverable: { id: string; creative: { id: string } },
    youtubeChannel: YoutubeChannel,
  ): Promise<void> => {
    this.store.dispatch(incrementFullScreenSpinnerNecessity());

    try {
      this.store.dispatch(setLoadingMessage('message.creative_now_sending'));

      await this.youtubeWebApi.sendCreativeToYoutube({ creative: deliverable.creative, youtubeChannel });

      this.messageDispatcher.notifyUnimportant('message.creative_has_sent_to_youtube_channel', {
        action: 'message.creative_has_sent_to_youtube_channel.action',
        onClickAction: () => this.transitionToYoutubeChannel(youtubeChannel),
      });

      const uploadedAdPlatformKeys =
        this.store.getState().creativeSending.uploadedAdPlatformKeys.get(deliverable.id) || [];
      this.store.dispatch(setUploadedAdPlatformKeys(deliverable.id, [...uploadedAdPlatformKeys, 'google']));
    } catch (err) {
      this.errorPresenter.showAsNonFatal(err);
    } finally {
      this.store.dispatch(decrementFullScreenSpinnerNecessity());
    }
  };

  public addConnectedChannel = async () => {
    this.store.dispatch(incrementFullScreenSpinnerNecessity());
    try {
      const authCode = await this.youtubeChannelConnectionService.authenticate();
      const channel = await this.youtubeWebApi.addConnectedChannel(authCode);
      this.store.dispatch(addYoutubeChannel(channel));
    } catch (err) {
      if (err instanceof ChannelUnlinkedError) {
        this.showAsNotification(err);
      } else {
        this.errorPresenter.showAsNonFatal(err);
      }
    } finally {
      this.store.dispatch(decrementFullScreenSpinnerNecessity());
    }
  };

  public disposeYoutubeChannel = async (channel: YoutubeChannel): Promise<void> => {
    this.store.dispatch(incrementFullScreenSpinnerNecessity());
    try {
      const success = await this.youtubeWebApi.revokeChannel(channel.channelId);
      if (success) {
        this.store.dispatch(disposeYoutubeChannel(channel));
      }
    } catch (err) {
      this.errorPresenter.showAsNonFatal(err);
    } finally {
      this.store.dispatch(decrementFullScreenSpinnerNecessity());
    }
  };

  public disposeYoutubeChannels = (): void => {
    this.store.dispatch(disposeYoutubeChannels());
  };

  private transitionToYoutubeChannel = (youtubeChannel: YoutubeChannel) => {
    // Window.opener is read-only but it should be set as noopener.
    const w = window.open(`https://studio.youtube.com/channel/${youtubeChannel.channelId}/videos`, '_blank') as any;
    if (w) {
      w.opener = null;
    }
  };

  private showAsNotification = (err: any) => {
    this.errorPresenter.reportError(err);
    this.messageDispatcher.notifyUnimportant('message.google_youtube_channel_is_not_linked', {
      action: 'root.snackbar.close',
      isAutoHide: false,
      onClickAction: () => this.messageDispatcher.clearUnimportant(),
    });
  };
}

export default SendCreativeToYoutubeUsecase;
